import { Component } from '@theme/component';
import {
  debounce,
  onDocumentLoaded,
  requestIdleCallback,
  setHeaderMenuStyle,
} from '@theme/utilities';

/** Max layout retries when submenu content is not yet painted. */
const LAYOUT_RETRY_LIMIT = 8;

/** Selector for measuring rendered mega menu content. */
const SUBMENU_CONTENT_SELECTOR =
  '.menu-list__submenu-inner, .mega-menu, .emenlen-mega-menu__list, .mega-menu__list';

/**
 * @typedef {Object} SubmenuContext
 * @property {HTMLElement} submenu
 * @property {boolean} isDefaultSlot
 * @property {boolean} hasSubmenu
 */

/**
 * Header navigation with click-to-toggle submenus.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {State} */
  #state = { activeItem: null };

  /** @type {WeakMap<HTMLElement, number>} */
  #heightCache = new WeakMap();

  /** @type {SubmenuContext | null} */
  #layoutContext = null;

  /** @type {number} */
  #layoutRaf = 0;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #appliedHeight = -1;

  /** @type {HTMLElement | null} */
  #appliedSubmenu = null;

  /** @type {HTMLElement | null} */
  #ignoreNextClickItem = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(() => {
      this.#preloadImages();
      requestIdleCallback(() => this.#warmSubmenuHeights(), { timeout: 1500 });
    });

    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    this.#cancelLayout();
    this.#disconnectResizeObserver();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#heightCache = new WeakMap();
    requestIdleCallback(() => this.#warmSubmenuHeights(), { timeout: 500 });
  }, 100);

  #onDocumentClick = (event) => {
    if (!(event.target instanceof Element) || !this.#state.activeItem) return;
    if (this.contains(event.target)) return;
    this.#deactivate();
  };

  #onDocumentKeydown = (event) => {
    if (event.key === 'Escape' && this.#state.activeItem) {
      this.#deactivate();
    }
  };

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Toggle submenu open/closed on click or keyboard.
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);
    if (!item) return;

    if (event.type === 'click' && item === this.#ignoreNextClickItem) {
      this.#ignoreNextClickItem = null;
      return;
    }

    const isKeyboardToggle = event.type === 'keydown';
    if (isKeyboardToggle) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    const isClick = event.type === 'click' || isKeyboardToggle;
    const listItem = event.target.closest('.menu-list__list-item');
    const isDefaultSlot = !listItem?.matches('[slot="overflow"], [slot="more"]');
    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (isClick && hasSubmenu) {
      event.preventDefault();
      if (item === this.#state.activeItem) {
        this.#deactivate();
        return;
      }
    } else if (isClick && !hasSubmenu && isDefaultSlot) {
      return;
    }

    if (!isClick) return;

    this.#cancelLayout();
    this.#disconnectResizeObserver();

    const previousItem = this.#state.activeItem;
    if (previousItem) {
      previousItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previousItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();
    this.style.setProperty('--submenu-opacity', '1');

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
      this.#openSubmenu(submenu, { submenu, isDefaultSlot, hasSubmenu });
    } else {
      this.#resetSubmenuDimensions();
    }

    if (isKeyboardToggle && hasSubmenu) {
      this.#ignoreNextClickItem = item;
    }
  };

  /**
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    this.#cancelLayout();
    this.#disconnectResizeObserver();
    this.#resetSubmenuDimensions();

    const submenu = findSubmenu(item);
    if (submenu) delete submenu.dataset.active;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    this.dataset.overflowExpanded = 'false';
    this.style.setProperty('--submenu-opacity', '0');
    this.#appliedHeight = -1;
    this.#appliedSubmenu = null;
  };

  /**
   * @param {HTMLElement} submenu
   * @param {SubmenuContext} context
   */
  #openSubmenu(submenu, context) {
    const cached = this.#heightCache.get(submenu);
    if (cached) {
      this.#applySubmenuHeight(cached, context);
      this.#watchSubmenuResize(context);
      return;
    }

    this.headerComponent?.setAttribute('data-submenu-measuring', '');
    this.#layoutContext = { ...context, attempts: 0 };
    this.#scheduleLayoutMeasure();
  }

  #scheduleLayoutMeasure() {
    cancelAnimationFrame(this.#layoutRaf);
    this.#layoutRaf = requestAnimationFrame(() => {
      this.#layoutRaf = requestAnimationFrame(() => this.#runLayoutMeasure());
    });
  }

  #runLayoutMeasure() {
    const context = this.#layoutContext;
    if (!context || findSubmenu(this.#state.activeItem) !== context.submenu) return;

    const height = this.#readSubmenuHeight(context.submenu, context.isDefaultSlot, context.hasSubmenu);

    if (height > 0) {
      this.#heightCache.set(context.submenu, height);
      this.#applySubmenuHeight(height, context);
      this.headerComponent?.removeAttribute('data-submenu-measuring');
      this.#layoutContext = null;
      this.#watchSubmenuResize(context);
      return;
    }

    context.attempts += 1;
    if (context.attempts < LAYOUT_RETRY_LIMIT) {
      this.#scheduleLayoutMeasure();
      return;
    }

    this.headerComponent?.removeAttribute('data-submenu-measuring');
    this.#layoutContext = null;
  }

  /**
   * @param {SubmenuContext} context
   */
  #watchSubmenuResize(context) {
    this.#disconnectResizeObserver();

    this.#resizeObserver = new ResizeObserver(() => {
      if (findSubmenu(this.#state.activeItem) !== context.submenu) return;

      const height = this.#readSubmenuHeight(context.submenu, context.isDefaultSlot, context.hasSubmenu);
      if (height > 0) {
        this.#heightCache.set(context.submenu, height);
        this.#applySubmenuHeight(height, context);
      }
    });

    this.#resizeObserver.observe(context.submenu);
    const inner = context.submenu.querySelector('.menu-list__submenu-inner');
    if (inner instanceof HTMLElement) {
      this.#resizeObserver.observe(inner);
    }
  }

  /**
   * @param {number} height
   * @param {SubmenuContext} context
   */
  #applySubmenuHeight(height, context) {
    if (height === this.#appliedHeight && context.submenu === this.#appliedSubmenu) return;

    this.#appliedHeight = height;
    this.#appliedSubmenu = context.submenu;

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
    this.headerComponent?.style.setProperty(
      '--full-open-header-height',
      `${height + headerVisibleHeight}px`
    );
  }

  #resetSubmenuDimensions() {
    this.headerComponent?.removeAttribute('data-submenu-measuring');
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.headerComponent?.style.setProperty('--full-open-header-height', '0px');
  }

  /**
   * @param {HTMLElement} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @returns {number}
   */
  #readSubmenuHeight(submenu, isDefaultSlot, hasSubmenu) {
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        return Math.max(this.overflowMenu?.offsetHeight || 0, overflowListHeight);
      }
      return overflowListHeight;
    }

    const inner = submenu.querySelector('.menu-list__submenu-inner');
    if (inner instanceof HTMLElement && inner.offsetHeight > 0) {
      return inner.offsetHeight;
    }

    const content = submenu.querySelector(SUBMENU_CONTENT_SELECTOR);
    if (content instanceof HTMLElement && content.offsetHeight > 0) {
      return content.offsetHeight;
    }

    return submenu.offsetHeight > 0 ? submenu.offsetHeight : 0;
  }

  #warmSubmenuHeights() {
    for (const node of this.querySelectorAll('[ref="submenu[]"]')) {
      if (!(node instanceof HTMLElement)) continue;

      const height = this.#readSubmenuHeight(node, true, true);
      if (height > 0) this.#heightCache.set(node, height);
    }
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    const submenus = slottedMenuLinks.flatMap((link) => {
      const submenu = link.querySelector('[ref="submenu[]"]');
      return submenu instanceof HTMLElement ? [submenu] : [];
    });

    submenus.forEach((submenu) => submenu.style.setProperty('display', 'none'));
    const height = this.overflowMenu?.offsetHeight || 0;
    submenus.forEach((submenu) => submenu.style.removeProperty('display'));

    return height;
  }

  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlap = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    if (isOverlap && this.headerComponent.offsetHeight > 0) {
      return (
        /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))
          ?.offsetHeight ?? 0
      );
    }

    return this.headerComponent.offsetHeight;
  }

  #preloadImages = () => {
    this.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.removeAttribute('loading');
    });
  };

  #cancelLayout() {
    cancelAnimationFrame(this.#layoutRaf);
    this.#layoutRaf = 0;
    this.#layoutContext = null;
  }

  #disconnectResizeObserver() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreItem = element.closest('[slot="more"]');
  if (moreItem) {
    return findMenuItem(moreItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[slot="overflow"]')) {
    return findMenuItem(element.querySelector('[ref="menuitem"]'));
  }

  const menuitem = element.closest('[ref="menuitem"]');
  if (menuitem instanceof HTMLElement) return menuitem;

  const listItem = element.closest('.menu-list__list-item');
  const menuitemFromList = listItem?.querySelector('[ref="menuitem"]');
  return menuitemFromList instanceof HTMLElement ? menuitemFromList : null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
